import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRoute } from 'wouter';
import { api, ApiError } from '../lib/api';
import {
  useInviteMember,
  useProjectMembers,
  useRemoveMember,
  type Project,
  type ProjectMember,
} from '../lib/projects';
import { ProjectHeader } from '../components/ProjectHeader';

export function TeamPage() {
  const [, params] = useRoute('/projects/:id/team');
  const id = params?.id;
  const project = useQuery({
    enabled: !!id,
    queryKey: ['projects', id],
    queryFn: () => api<{ project: Project }>(`/api/projects/${id}`).then((r) => r.project),
  });

  if (!id) return null;
  const isOwner = project.data?.role === 'owner';

  return (
    <div className="min-h-full">
      <ProjectHeader projectId={id} page="TEAM" />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-4 md:py-8 space-y-6">
        <section className="rounded-[1.5rem] border border-border-color bg-surface p-4 md:p-6 overflow-hidden relative">
          <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-accent-cyan/10 to-transparent pointer-events-none" />
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-accent-cyan mb-3">Collaborative brand operation</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight max-w-2xl">Invite the people who actually run this brand.</h1>
          <p className="mt-3 text-sm text-muted max-w-2xl leading-relaxed">
            Bubbles is multi-brand and invite-only first. Owners configure the brand and invite co-creators; members can brief the content team, review drafts, download finished work, and mark posts as published.
          </p>
        </section>

        <section className="grid md:grid-cols-2 gap-4">
          <RoleCard title="Owner" icon="crown" items={["Controls strategy and settings", "Invites and removes co-creators", "Owns the brand memory"]} active={isOwner} />
          <RoleCard title="Co-creator" icon="diversity_3" items={["Briefs the content team", "Reviews and publishes posts", "Keeps the content queue moving"]} active={project.data?.role === 'member'} />
        </section>

        {isOwner && <InviteForm projectId={id} />}
        <MembersList projectId={id} ownerView={isOwner} />
      </main>
    </div>
  );
}

function InviteForm({ projectId }: { projectId: string }) {
  const invite = useInviteMember(projectId);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'owner' | 'member'>('member');
  const [message, setMessage] = useState<{ tone: 'emerald' | 'red'; text: string } | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setMessage(null);
    try {
      const result = await invite.mutateAsync({ email: trimmed, role });
      setEmail('');
      setMessage({
        tone: 'emerald',
        text: result.userCreated
          ? `Invited ${result.member.email}. They can log in straight away — their account was pre-created.`
          : `Added ${result.member.email} as ${role}.`,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not invite. Try again.';
      setMessage({ tone: 'red', text: msg });
    }
  }

  return (
    <section className="rounded-xl border border-border-color bg-surface p-4 md:p-6">
      <header className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Invite</p>
        <h2 className="font-display text-xl font-bold mt-1">Add a co-creator</h2>
        <p className="text-xs text-muted mt-1 leading-relaxed">
          Their email becomes their login. If they haven't signed in to Bubbles yet, we'll pre-create their account so the invite works immediately.
        </p>
      </header>
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <label htmlFor="invite-email" className="font-mono text-[10px] uppercase tracking-wider text-muted block mb-1.5">
            Email
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@example.com"
            className="w-full bg-surface-2 border border-border-color rounded px-3 py-2 text-sm font-mono text-text-primary focus:outline-none focus:border-accent-cyan transition-colors"
          />
        </div>
        <div>
          <label htmlFor="invite-role" className="font-mono text-[10px] uppercase tracking-wider text-muted block mb-1.5">
            Role
          </label>
          <select
            id="invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'owner' | 'member')}
            className="bg-surface-2 border border-border-color rounded px-3 py-2 text-sm font-mono text-text-primary focus:outline-none focus:border-accent-cyan transition-colors"
          >
            <option value="member">Co-creator</option>
            <option value="owner">Owner</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={!email.trim() || invite.isPending}
          className="font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded bg-accent-cyan/15 text-accent-cyan border border-accent-cyan/40 hover:bg-accent-cyan/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {invite.isPending ? 'Inviting…' : 'Add to team'}
        </button>
      </form>
      {message && (
        <p className={`mt-3 font-mono text-[10px] uppercase tracking-wider ${message.tone === 'emerald' ? 'text-accent-emerald' : 'text-accent-red'}`}>
          {message.text}
        </p>
      )}
    </section>
  );
}

function MembersList({ projectId, ownerView }: { projectId: string; ownerView: boolean }) {
  const members = useProjectMembers(projectId);
  const remove = useRemoveMember(projectId);
  const [removeError, setRemoveError] = useState<string | null>(null);

  if (members.isLoading) {
    return <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Loading team…</p>;
  }
  const rows = members.data ?? [];

  return (
    <section className="rounded-xl border border-border-color bg-surface p-4 md:p-6">
      <header className="mb-4">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Current team</p>
        <h2 className="font-display text-xl font-bold mt-1">
          {rows.length} member{rows.length === 1 ? '' : 's'}
        </h2>
      </header>
      <ul className="divide-y divide-border-color">
        {rows.map((m: ProjectMember) => (
          <li key={m.userId} className="flex items-center justify-between gap-3 py-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm text-text-primary truncate">{m.email}</p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted mt-0.5">
                {m.role === 'owner' ? 'Owner' : 'Co-creator'} · joined {new Date(m.createdAt).toLocaleDateString()}
              </p>
            </div>
            {ownerView && (
              <button
                type="button"
                onClick={async () => {
                  setRemoveError(null);
                  try {
                    await remove.mutateAsync(m.userId);
                  } catch (err) {
                    setRemoveError(err instanceof ApiError ? err.message : 'Could not remove.');
                  }
                }}
                disabled={remove.isPending}
                className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded border border-accent-red/40 text-accent-red hover:bg-accent-red/10 disabled:opacity-40 transition-colors"
              >
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
      {removeError && (
        <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-accent-red">
          {removeError === 'cannot_remove_last_owner' ? "Can't remove the last owner. Promote someone else first." : removeError}
        </p>
      )}
    </section>
  );
}

function RoleCard({ title, icon, items, active }: { title: string; icon: string; items: string[]; active: boolean }) {
  return (
    <div className={`rounded-xl border p-5 ${active ? 'border-accent-cyan bg-accent-cyan/10' : 'border-border-color bg-surface'}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="material-symbols-outlined text-accent-cyan">{icon}</span>
        <h2 className="font-display text-xl font-bold">{title}</h2>
        {active && <span className="status-pill status-pill-cyan ml-auto">YOU</span>}
      </div>
      <ul className="space-y-2 text-sm text-muted">
        {items.map((item) => <li key={item}>• {item}</li>)}
      </ul>
    </div>
  );
}
